/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {ALL, DISMISS_MSG} from '../../../../common/constants';
import {GetTransmissionOptionsRequest} from '../../../../models/get-transmission-options-request';
import {UpdateTransmissionAttributeRequest} from '../../../../models/update-transmission-attribute-request';
import {ExcelExportService} from '../../../../services/excel-export.service';
import {ManageTransmissionAttributeService} from '../../../../services/transmission-attribute.service';
import {UpdateAttributePopupComponent} from '../update-attribute-popup/update-attribute-popup.component';

interface Dropdown {
  form: FormControl;
  filteredOptions: Observable<string[]>;
  options: string[];
  selectedOption: string;
}

@Component({
    selector: 'app-manage-transmissions',
    templateUrl: './manage-transmissions.component.html',
    styleUrls: ['./manage-transmissions.component.scss'],
    standalone: false
})
export class ManageTransmissionsComponent implements OnInit {
  showSpinner: boolean;
  ALL = ALL;

  family: Dropdown = {
    form: new FormControl(),
    filteredOptions: null,
    options: [],
    selectedOption: '%'
  };
  rpo: Dropdown = {
    form: new FormControl(),
    filteredOptions: null,
    options: [],
    selectedOption: '%'
  };
  name: Dropdown = {
    form: new FormControl(),
    filteredOptions: null,
    options: [],
    selectedOption: '%'
  };
  transmissionType: Dropdown = {
    form: new FormControl(),
    filteredOptions: null,
    options: [],
    selectedOption: '%'
  };
  speed: Dropdown = {
    form: new FormControl(),
    filteredOptions: null,
    options: [],
    selectedOption: '%'
  };
  driveType: Dropdown = {
    form: new FormControl(),
    filteredOptions: null,
    options: [],
    selectedOption: '%'
  };

  oldValueInput: FormControl = new FormControl('');
  newValueInput: FormControl = new FormControl('', [Validators.required]);

  lastValidFamily = this.ALL;
  lastValidDriveType = this.ALL;

  dropdowns: Dropdown[] = [];

  attributeToUpdate = '';
  updateOptions: string[] = ['Trans Family', 'Drive Type'];
  allowUpdate = false;

  constructor(public snackBar: FabMessageService,
              public dialog: MatDialog,
              private excelExport: ExcelExportService,
              private readonly transmissionService: ManageTransmissionAttributeService) {
  }

  ngOnInit(): void {
    this.showSpinner = true;
    this.dropdowns.push(this.family);
    this.dropdowns.push(this.rpo);
    this.dropdowns.push(this.name);
    this.dropdowns.push(this.transmissionType);
    this.dropdowns.push(this.speed);
    this.dropdowns.push(this.driveType);

    // Disable dropdowns until loading finishes
    this.dropdowns.forEach(dropdown => {
      dropdown.form.disable();
    });

    // User can only read, not modify the old value input
    this.oldValueInput.disable();

    this.initializeOptions();
  }

  /**
   * Get data for dropdown options.
   * Set default value for the dropdowns.
   */
  initializeOptions(): void {
    // Set all initial options
    this.transmissionService.getAllOptions().subscribe(res => {
      this.updateDropdown(this.family, res.familyOptions);
      this.updateDropdown(this.rpo, res.rpoOptions);
      this.updateDropdown(this.name, res.nameOptions);
      this.updateDropdown(this.transmissionType, res.transTypeOptions);
      this.updateDropdown(this.speed, res.speedOptions);
      this.updateDropdown(this.driveType, res.driveTypeOptions);

      // Select "All" by default
      this.family.form.setValue(this.ALL);
      this.rpo.form.setValue(this.ALL);
      this.name.form.setValue(this.ALL);
      this.transmissionType.form.setValue(this.ALL);
      this.speed.form.setValue(this.ALL);
      this.driveType.form.setValue(this.ALL);

      // Enable dropdowns once loading finishes
      this.dropdowns.forEach(dropdown => {
        dropdown.form.enable();
      });
      this.showSpinner = false;
    }, err => {
      this.snackBar.displayError(err, DISMISS_MSG);
    });

    this.updateAllFilteredDropdowns();
  }

  /**
   * For every selection changed, filter the dropdowns below.
   * @param attributeChanged change in selected dropdown.
   * @param menu change in selection for a menu.
   */
  onChangeSelection(attributeChanged: string, menu: Dropdown): void {
    this.dropdowns.forEach( dropDown => {
      if (this.dropdowns.indexOf(menu) < this.dropdowns.indexOf(dropDown)) {
          dropDown.selectedOption = '%';
      }
    });
    menu.selectedOption = menu.form.value;
    this.dispatchOptionsRequestAndUpdateDropdowns(attributeChanged);

  }

  /**
   *  No need to filter on last dropdown, only update the new value input since drive type is an updatable field option
   */
  onChangeDriveType(): void {
    this.driveType.selectedOption = this.driveType.form.value;
    this.lastValidDriveType = this.driveType.form.value;
    this.setLastValidUpdateValue();
  }

  /**
   * Open a popup to preview/commit changes.
   * @param title of the Filter option that will be updated.
   * @param currentValue is an existing value in DB that need to be updated.
   * @param attribute is a column name used to retrieve data from DB.
   */
  openDialog(title: string, currentValue: string, attribute: string): void {
    this.attributeToUpdate = attribute;
    const dialogRef = this.dialog.open(UpdateAttributePopupComponent, {
      data: {
        header: title,
        selectedValue: currentValue,
        attributeToUpdate: attribute,
        maxLength: 255
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      // received data from dialog-component
      if (res) {
        this.showSpinner = true;
        this.makeChanges(!res.data.isPreviewChange, currentValue, res.data.newValue);
      }
    });
  }

  /**
   * Set parameters for API call.
   * @param attributeChanged dropdown updated by user.
   */
  buildOptionsRequestAndCascadeUpdate(attributeChanged: string): GetTransmissionOptionsRequest {
    const req: GetTransmissionOptionsRequest = {
      attribute: attributeChanged,
      selectedFamily: '%',
      selectedRPO: '%',
      selectedName: '%',
      selectedTransmissionType: '%',
      selectedSpeed: '%',
      selectedDriveType: '%'
    };

    // Set only the applicable attributes in the req and autoselect the first valid option for the subsequent dropdowns
    switch (attributeChanged.toUpperCase()) {
      case 'TRANFAM':
        req.selectedFamily = this.family.selectedOption;
        this.lastValidFamily = this.family.selectedOption;
        this.cascadeUpdateDropdowns(1);
        break;
      case 'TRANRPO':
        req.selectedFamily = this.family.selectedOption;
        req.selectedRPO = this.rpo.selectedOption;
        this.cascadeUpdateDropdowns(2);
        break;
      case 'TRANSMISSIONNAME':
        req.selectedFamily = this.family.selectedOption;
        req.selectedRPO = this.rpo.selectedOption;
        req.selectedName = this.name.selectedOption;
        this.cascadeUpdateDropdowns(3);
        break;
      case 'TRANTYPE':
        req.selectedFamily = this.family.selectedOption;
        req.selectedRPO = this.rpo.selectedOption;
        req.selectedName = this.name.selectedOption;
        req.selectedTransmissionType = this.transmissionType.selectedOption;
        this.cascadeUpdateDropdowns(4);
        break;
      case 'TRANSPEED':
        req.selectedFamily = this.family.selectedOption;
        req.selectedRPO = this.rpo.selectedOption;
        req.selectedName = this.name.selectedOption;
        req.selectedTransmissionType = this.transmissionType.selectedOption;
        req.selectedSpeed = this.speed.selectedOption;
        this.cascadeUpdateDropdowns(5);
        break;
      case 'DRIVETYPE':
        req.selectedFamily = this.family.selectedOption;
        req.selectedRPO = this.rpo.selectedOption;
        req.selectedName = this.name.selectedOption;
        req.selectedTransmissionType = this.transmissionType.selectedOption;
        req.selectedSpeed = this.speed.selectedOption;
        req.selectedDriveType = this.driveType.selectedOption;
        this.lastValidDriveType = this.driveType.selectedOption;
        break;
    }

    return req;
  }

  /**
   * Get data for all the dropdowns.
   * @param attributeChanged change in selected value for a dropdown.
   */
  dispatchOptionsRequestAndUpdateDropdowns(attributeChanged: string): void {
    const req = this.buildOptionsRequestAndCascadeUpdate(attributeChanged);

    this.transmissionService.getFilteredOptions(req).subscribe(res => {
      if (res.familyOptions) {
        if (res.familyOptions.length === 2) {
          this.lastValidFamily = res.familyOptions[1];
        } else {
          this.lastValidFamily = this.ALL;
        }
        this.updateDropdown(this.family, res.familyOptions);
      }

      if (res.rpoOptions) {
        this.updateDropdown(this.rpo, res.rpoOptions);
      }

      if (res.nameOptions) {
        this.updateDropdown(this.name, res.nameOptions);
      }

      if (res.transTypeOptions) {
        this.updateDropdown(this.transmissionType, res.transTypeOptions);
      }

      if (res.speedOptions) {
        this.updateDropdown(this.speed, res.speedOptions);
      }

      if (res.driveTypeOptions) {
        this.updateDropdown(this.driveType, res.driveTypeOptions);
        if (res.driveTypeOptions.length === 2) {
          this.lastValidDriveType = res.driveTypeOptions[1];
        } else {
          this.lastValidDriveType = this.ALL;
        }
        this.setLastValidUpdateValue();
      }

      // Update filtered options with new values
      this.updateAllFilteredDropdowns();
    }, err => {
      this.snackBar.displayError(err, DISMISS_MSG);
    });
  }

  /**
   * Update filter options for all dropdown.
   */
  updateAllFilteredDropdowns(): void {
    this.updateFilteredDropdownOptions(this.family);
    this.updateFilteredDropdownOptions(this.rpo);
    this.updateFilteredDropdownOptions(this.name);
    this.updateFilteredDropdownOptions(this.transmissionType);
    this.updateFilteredDropdownOptions(this.speed);
    this.updateFilteredDropdownOptions(this.driveType);
  }

  /**
   * On value change for model, filter available option based on users input.
   * @param dropdown selected dropdown.
   */
  updateFilteredDropdownOptions(dropdown: Dropdown): void {
    dropdown.filteredOptions = dropdown.form.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(dropdown.options, value || ''))
    );
  }

  /**
   * Updates a single dropdown, if only 1 option available other then all, then set that value as selected value.
   * @param dropdown dropdown option.
   * @param newOptions available options for the dropdown.
   */
  updateDropdown(dropdown: Dropdown, newOptions: string[]): void {
    dropdown.options = newOptions;
    if (!dropdown.options.includes(this.ALL)) {
      dropdown.options.unshift(this.ALL);
    }
    if (newOptions.length === 2) {
      // Autoselect first available option
      dropdown.form.setValue(dropdown.options[1]);
      dropdown.selectedOption = dropdown.options[1];
    } else {
      dropdown.form.setValue(this.ALL);
    }
  }

  /**
   * Updates dropdowns starting at the given index and "below".
   * @param startIndex, indicates which dropdown to start with.
   */
  cascadeUpdateDropdowns(startIndex: number): void {
    let dropdown: Dropdown;
    for (let i = startIndex; i < this.dropdowns.length; i++) {
      dropdown = this.dropdowns[i];

      if (dropdown.options.length === 2) {
        // If has only one valid option (excluding "ALL" option), then auto select the first non-All option
        dropdown.form.setValue(dropdown.options[1]);
      } else {
        dropdown.form.setValue(this.ALL);
      }
    }
  }

  /**
   * Filter available options based on user input.
   * @param options All available options.
   * @param value user input from search field.
   */
  _filter(options: string[], value: string): string[] {
    const filterValue = value.toLowerCase();
    return options.filter(option => filterValue === this.ALL.toLowerCase()
      ? option.toLowerCase().includes('') : option.toLowerCase().includes(filterValue));
  }

  /**
   * Set selected attribute to update.
   * @param attributeToUpdate selected attribute.
   */
  setAttributeToUpdate(attributeToUpdate: string): void {
    this.attributeToUpdate = attributeToUpdate;
    this.setLastValidUpdateValue();
  }

  /**
   * Keeps track of the last valid user-selected values, instead of using the value
   * that the user types into the dropdown filter.
   * This ensures that the Old Value input will be a valid, existing value to update.
   */
  setLastValidUpdateValue(): void {
    const attributeToUpdate = this.attributeToUpdate;
    if (attributeToUpdate.toUpperCase() === 'TRANS FAMILY' && this.lastValidFamily !== this.ALL) {
      this.oldValueInput.setValue(this.lastValidFamily);
      this.allowUpdate = true;
    } else if (attributeToUpdate.toUpperCase() === 'DRIVE TYPE' && this.lastValidDriveType !== this.ALL) {
      this.oldValueInput.setValue(this.lastValidDriveType);
      this.allowUpdate = true;
    } else {
      this.oldValueInput.setValue('');
      this.allowUpdate = false;
    }
  }

  /**
   * If doCommit, then update old value with new value, else preview data change.
   * @param doCommit is commit change or preview change?
   * @param oldValue current value.
   * @param newValue new value entered by user.
   */
  makeChanges(doCommit: boolean, oldValue: string, newValue: string): void {

    const req: UpdateTransmissionAttributeRequest = {
      attributeToUpdate: this.attributeToUpdate,
      currentValue: (oldValue === '' ? this.ALL : oldValue),
      newValue: (newValue === '' ? this.ALL : newValue),
      doCommit,
      selectedFamily: this.family.selectedOption,
      selectedRPO: this.rpo.selectedOption,
      selectedName: this.name.selectedOption,
      selectedTransmissionType: this.transmissionType.selectedOption,
      selectedSpeed: this.speed.selectedOption,
      selectedDriveType: this.driveType.selectedOption
    };

    let fileName = 'ChangeAttribute_Tran';
    if (!doCommit) {
      fileName += '_PREVIEW';
    }
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    fileName += '_' + currDate;

    this.transmissionService.updateAttribute(req, fileName).subscribe(res => {
      this.showSpinner = false;
      this.excelExport.exportToExcelFromServer(res, fileName);
    }, err => {
      this.showSpinner = false;
      this.snackBar.displayError(err, DISMISS_MSG);
    }, () => {
      if (doCommit) {
        this.resetOptions();
      }
    });
  }

  /**
   * Reset all dropdown selections
   */
  resetOptions(): void {
    this.initializeOptions();
    this.lastValidDriveType = this.ALL;
    this.lastValidFamily = this.ALL;
    this.oldValueInput.reset();
    this.newValueInput.reset();
    this.resetSelectedValues();

  }

  resetSelectedValues(): void {
    this.family.selectedOption = '%';
    this.rpo.selectedOption = '%';
    this.name.selectedOption = '%';
    this.transmissionType.selectedOption = '%';
    this.speed.selectedOption = '%';
    this.driveType.selectedOption = '%';
  }

  /**
   * If dropdown value is All, then set text field value to empty on click.
   * @param menu selected dropdown
   */
  resetText(menu: Dropdown): void {
    if (menu.form.value === ALL) {
      menu.form.setValue('');
    }
  }

}
